import React, { useState, useEffect, useMemo } from 'react';

function Production() {
  // Данные
  const [products, setProducts] = useState(() => {
    const saved = localStorage.getItem('khepri_products');
    return saved ? JSON.parse(saved) : [
      { id: 1, name: 'Стул офисный', sku: 'CH-001', materials: 'Дерево, ткань, саморезы', labor: '2 часа', cost: 1200, price: 3500 },
      { id: 2, name: 'Стол письменный', sku: 'TB-002', materials: 'ЛДСП, кромка, саморезы', labor: '4 часа', cost: 2800, price: 7500 },
      { id: 3, name: 'Полка настенная', sku: 'SH-003', materials: 'ЛДСП, крепёж', labor: '1 час', cost: 400, price: 1200 },
      { id: 4, name: 'Шкаф распашной', sku: 'CB-004', materials: 'ЛДСП, кромка, фурнитура', labor: '8 часов', cost: 6500, price: 16000 },
    ];
  });

  const [orders, setOrders] = useState(() => {
    const saved = localStorage.getItem('khepri_orders');
    return saved ? JSON.parse(saved) : [
      { id: 1, product: 'Стул офисный', quantity: 10, status: 'В производстве', progress: 60 },
      { id: 2, product: 'Стол письменный', quantity: 5, status: 'Запланирован', progress: 0 },
      { id: 3, product: 'Шкаф распашной', quantity: 2, status: 'Завершён', progress: 100 },
    ];
  });

  // UI state
  const [showProductForm, setShowProductForm] = useState(false);
  const [showOrderForm, setShowOrderForm] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [orderFilter, setOrderFilter] = useState('all');
  const [sortBy, setSortBy] = useState('margin_desc');
  const [toast, setToast] = useState('');

  // Формы
  const [newProduct, setNewProduct] = useState({ name: '', sku: '', materials: '', labor: '', cost: '', price: '' });
  const [newOrder, setNewOrder] = useState({ product: '', quantity: '1' });

  // Сохранение
  useEffect(() => {
    localStorage.setItem('khepri_products', JSON.stringify(products));
  }, [products]);

  useEffect(() => {
    localStorage.setItem('khepri_orders', JSON.stringify(orders));
  }, [orders]);

  // Toast auto-hide
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const showToast = (message) => setToast(message);

  // Фильтрация и сортировка продуктов
  const filteredProducts = useMemo(() => {
    let filtered = products.filter(p =>
      p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.sku.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.materials.toLowerCase().includes(searchQuery.toLowerCase())
    );

    if (sortBy === 'name') filtered.sort((a, b) => a.name.localeCompare(b.name));
    else if (sortBy === 'margin_desc') filtered.sort((a, b) => (b.price - b.cost) - (a.price - a.cost));
    else if (sortBy === 'margin_asc') filtered.sort((a, b) => (a.price - a.cost) - (b.price - b.cost));
    else if (sortBy === 'price_desc') filtered.sort((a, b) => b.price - a.price);

    return filtered;
  }, [products, searchQuery, sortBy]);

  // Фильтрация заказов
  const filteredOrders = useMemo(() => {
    if (orderFilter === 'all') return orders;
    return orders.filter(o => o.status === orderFilter);
  }, [orders, orderFilter]);

  // Статистика
  const stats = useMemo(() => {
    const totalMargin = products.reduce((sum, p) => sum + (p.price - p.cost), 0);
    const avgMargin = products.length > 0 ? totalMargin / products.length : 0;
    const activeOrders = orders.filter(o => o.status !== 'Завершён').length;
    const completedOrders = orders.filter(o => o.status === 'Завершён').length;
    return {
      totalProducts: products.length,
      avgMargin: Math.round(avgMargin),
      totalMargin,
      activeOrders,
      completedOrders,
    };
  }, [products, orders]);

  // Действия
  const addProduct = () => {
    if (!newProduct.name.trim() || !newProduct.sku.trim()) {
      showToast('⚠️ Заполните название и артикул');
      return;
    }
    const cost = parseFloat(newProduct.cost) || 0;
    const price = parseFloat(newProduct.price) || 0;
    if (cost < 0 || price < 0) {
      showToast('⚠️ Значения не могут быть отрицательными');
      return;
    }
    const product = {
      id: Date.now(),
      name: newProduct.name.trim(),
      sku: newProduct.sku.trim(),
      materials: newProduct.materials.trim() || 'Не указаны',
      labor: newProduct.labor.trim() || 'Не указан',
      cost,
      price,
    };
    setProducts(prev => [...prev, product]);
    setNewProduct({ name: '', sku: '', materials: '', labor: '', cost: '', price: '' });
    setShowProductForm(false);
    showToast('✅ Продукт добавлен');
  };

  const addOrder = () => {
    if (!newOrder.product || !newOrder.quantity) {
      showToast('⚠️ Выберите продукт и укажите количество');
      return;
    }
    const order = {
      id: Date.now(),
      product: newOrder.product,
      quantity: parseInt(newOrder.quantity) || 1,
      status: 'Запланирован',
      progress: 0,
    };
    setOrders(prev => [...prev, order]);
    setNewOrder({ product: '', quantity: '1' });
    setShowOrderForm(false);
    showToast('✅ Заказ создан');
  };

  const deleteProduct = (id) => {
    if (window.confirm('Удалить продукт?')) {
      setProducts(prev => prev.filter(p => p.id !== id));
      showToast('🗑️ Продукт удалён');
    }
  };

  const deleteOrder = (id) => {
    if (window.confirm('Удалить заказ?')) {
      setOrders(prev => prev.filter(o => o.id !== id));
      showToast('🗑️ Заказ удалён');
    }
  };

  const updateOrderProgress = (id, delta) => {
    setOrders(prev => prev.map(o => {
      if (o.id !== id) return o;
      const newProgress = Math.min(Math.max(o.progress + delta, 0), 100);
      const newStatus = newProgress === 100 ? 'Завершён' : newProgress > 0 ? 'В производстве' : 'Запланирован';
      return { ...o, progress: newProgress, status: newStatus };
    }));
  };

  const setProgress = (id, value) => {
    const newProgress = Math.min(Math.max(value, 0), 100);
    setOrders(prev => prev.map(o => {
      if (o.id !== id) return o;
      const newStatus = newProgress === 100 ? 'Завершён' : newProgress > 0 ? 'В производстве' : 'Запланирован';
      return { ...o, progress: newProgress, status: newStatus };
    }));
  };

  const exportCSV = () => {
    const rows = [
      ['Продукт', 'Артикул', 'Материалы', 'Труд', 'Себестоимость', 'Цена', 'Маржа'],
      ...products.map(p => [p.name, p.sku, p.materials, p.labor, p.cost, p.price, p.price - p.cost]),
    ];
    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'Продукция_Khepri.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    showToast('📥 CSV экспортирован');
  };

  // Helpers
  const getStatusColor = (status) => {
    if (status === 'Завершён') return '#10B981';
    if (status === 'В производстве') return '#F59E0B';
    return '#94A3B8';
  };

  const getProgressColor = (progress) => {
    if (progress >= 100) return '#10B981';
    if (progress >= 50) return '#F59E0B';
    return '#F7DE8A';
  };

  const getMarginColor = (cost, price) => {
    const margin = price - cost;
    if (margin <= 0) return '#EF4444';
    if (margin < cost * 0.3) return '#F59E0B';
    return '#10B981';
  };

  const getMarginPercent = (cost, price) => {
    if (cost === 0) return 0;
    return Math.round(((price - cost) / cost) * 100);
  };

  return (
    <div>
      {/* Toast */}
      {toast && (
        <div style={{ position: 'fixed', top: 20, right: 20, background: '#F7DE8A', color: '#0F172A', padding: '12px 20px', borderRadius: 8, fontWeight: 700, zIndex: 2000, boxShadow: '0 4px 12px rgba(0,0,0,0.3)', animation: 'fadeIn 0.3s' }}>
          {toast}
        </div>
      )}

      {/* Шапка */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 16 }}>
        <h2 style={{ color: '#F7DE8A', margin: 0 }}>⚙️ Управление производством</h2>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button onClick={() => setShowProductForm(!showProductForm)} style={btnPrimary}>
            {showProductForm ? '✕ Закрыть' : '+ Продукт'}
          </button>
          <button onClick={() => setShowOrderForm(!showOrderForm)} style={btnSecondary}>
            {showOrderForm ? '✕ Закрыть' : '+ Заказ'}
          </button>
          <button onClick={exportCSV} style={btnDark}>
            📥 CSV
          </button>
        </div>
      </div>

      {/* Статистика */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10, marginBottom: 20 }}>
        <div style={statCard}>
          <div style={statLabel}>Продуктов</div>
          <div style={{...statValue, color: '#F7DE8A'}}>{stats.totalProducts}</div>
        </div>
        <div style={statCard}>
          <div style={statLabel}>Общая маржа</div>
          <div style={{...statValue, color: '#10B981'}}>+{stats.totalMargin.toLocaleString()} ₽</div>
        </div>
        <div style={statCard}>
          <div style={statLabel}>Средняя маржа</div>
          <div style={{...statValue, color: '#10B981'}}>+{stats.avgMargin.toLocaleString()} ₽</div>
        </div>
        <div style={statCard}>
          <div style={statLabel}>Активные</div>
          <div style={{...statValue, color: '#F59E0B'}}>{stats.activeOrders}</div>
        </div>
        <div style={statCard}>
          <div style={statLabel}>Завершено</div>
          <div style={{...statValue, color: '#10B981'}}>{stats.completedOrders}</div>
        </div>
      </div>

      {/* Поиск и сортировка */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <input
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          placeholder="🔍 Поиск по названию, артикулу, материалам..."
          style={{ flex: 1, minWidth: 200, padding: 10, background: '#0F172A', border: '1px solid #8A6636', borderRadius: 8, color: '#F8FAFC', fontSize: 13 }}
        />
        <select value={sortBy} onChange={e => setSortBy(e.target.value)} style={selectStyle}>
          <option value="margin_desc">Сначала выгодные</option>
          <option value="margin_asc">Сначала невыгодные</option>
          <option value="price_desc">По цене ↓</option>
          <option value="name">По названию</option>
        </select>
      </div>

      {/* Форма продукта */}
      {showProductForm && (
        <div style={formCard}>
          <h3 style={{ marginBottom: 12, color: '#F7DE8A' }}>Новый продукт</h3>
          <div style={formGrid}>
            <input value={newProduct.name} onChange={e => setNewProduct({...newProduct, name: e.target.value})} placeholder="Название *" style={inputStyle} />
            <input value={newProduct.sku} onChange={e => setNewProduct({...newProduct, sku: e.target.value})} placeholder="Артикул *" style={inputStyle} />
            <input value={newProduct.labor} onChange={e => setNewProduct({...newProduct, labor: e.target.value})} placeholder="Трудозатраты" style={inputStyle} />
            <input value={newProduct.materials} onChange={e => setNewProduct({...newProduct, materials: e.target.value})} placeholder="Материалы (через запятую)" style={{...inputStyle, gridColumn: 'span 3'}} />
            <input value={newProduct.cost} onChange={e => setNewProduct({...newProduct, cost: e.target.value})} placeholder="Себестоимость (₽)" type="number" min="0" style={inputStyle} />
            <input value={newProduct.price} onChange={e => setNewProduct({...newProduct, price: e.target.value})} placeholder="Цена (₽)" type="number" min="0" style={inputStyle} />
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, color: '#94A3B8' }}>
              Маржа: <strong style={{ marginLeft: 6, color: getMarginColor(parseFloat(newProduct.cost) || 0, parseFloat(newProduct.price) || 0) }}>
                {((parseFloat(newProduct.price) || 0) - (parseFloat(newProduct.cost) || 0)).toLocaleString()} ₽
              </strong>
            </div>
          </div>
          <button onClick={addProduct} style={{...btnPrimary, marginTop: 12}}>Добавить продукт</button>
        </div>
      )}

      {/* Форма заказа */}
      {showOrderForm && (
        <div style={formCard}>
          <h3 style={{ marginBottom: 12, color: '#F7DE8A' }}>Новый заказ</h3>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <select value={newOrder.product} onChange={e => setNewOrder({...newOrder, product: e.target.value})} style={{...selectStyle, flex: 1, minWidth: 200 }}>
              <option value="">Выберите продукт...</option>
              {products.map(p => <option key={p.id} value={p.name}>{p.name}</option>)}
            </select>
            <input value={newOrder.quantity} onChange={e => setNewOrder({...newOrder, quantity: e.target.value})} placeholder="Кол-во" type="number" min="1" style={{...inputStyle, width: 100}} />
            <button onClick={addOrder} style={btnPrimary}>Создать заказ</button>
          </div>
        </div>
      )}

      {/* Спецификации */}
      <h3 style={{ marginBottom: 10, color: '#F7DE8A' }}>📋 Спецификации ({filteredProducts.length})</h3>
      <div style={{ overflowX: 'auto', marginBottom: 24 }}>
        <table style={tableStyle}>
          <thead>
            <tr style={{ color: '#94A3B8', textAlign: 'left', background: '#1E293B' }}>
              <th style={{ padding: 8 }}>Продукт</th>
              <th style={{ padding: 8 }}>Артикул</th>
              <th style={{ padding: 8 }}>Материалы</th>
              <th style={{ padding: 8 }}>Труд</th>
              <th style={{ padding: 8, textAlign: 'right' }}>Себест.</th>
              <th style={{ padding: 8, textAlign: 'right' }}>Цена</th>
              <th style={{ padding: 8, textAlign: 'right' }}>Маржа</th>
              <th style={{ padding: 8, textAlign: 'right' }}>%</th>
              <th style={{ padding: 8 }}>Действие</th>
            </tr>
          </thead>
          <tbody>
            {filteredProducts.map((p) => (
              <tr key={p.id} style={{ borderBottom: '1px solid #8A6636' }}>
                <td style={{ padding: 8 }}>{p.name}</td>
                <td style={{ padding: 8, color: '#94A3B8' }}>{p.sku}</td>
                <td style={{ padding: 8, color: '#94A3B8', fontSize: 11 }}>{p.materials}</td>
                <td style={{ padding: 8, color: '#94A3B8' }}>{p.labor}</td>
                <td style={{ padding: 8, textAlign: 'right' }}>{p.cost.toLocaleString()} ₽</td>
                <td style={{ padding: 8, textAlign: 'right' }}>{p.price.toLocaleString()} ₽</td>
                <td style={{ padding: 8, textAlign: 'right', color: getMarginColor(p.cost, p.price), fontWeight: 700 }}>
                  +{(p.price - p.cost).toLocaleString()} ₽
                </td>
                <td style={{ padding: 8, textAlign: 'right', color: getMarginColor(p.cost, p.price), fontWeight: 600 }}>
                  {getMarginPercent(p.cost, p.price)}%
                </td>
                <td style={{ padding: 8 }}>
                  <button onClick={() => deleteProduct(p.id)} style={deleteBtn}>🗑️</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Заказы */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 10 }}>
        <h3 style={{ color: '#F7DE8A', margin: 0 }}>📦 Заказы ({filteredOrders.length})</h3>
        <select value={orderFilter} onChange={e => setOrderFilter(e.target.value)} style={selectStyle}>
          <option value="all">Все статусы</option>
          <option value="Запланирован">Запланирован</option>
          <option value="В производстве">В производстве</option>
          <option value="Завершён">Завершён</option>
        </select>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={tableStyle}>
          <thead>
            <tr style={{ color: '#94A3B8', textAlign: 'left', background: '#1E293B' }}>
              <th style={{ padding: 8 }}>Заказ</th>
              <th style={{ padding: 8 }}>Продукт</th>
              <th style={{ padding: 8, textAlign: 'right' }}>Кол-во</th>
              <th style={{ padding: 8 }}>Статус</th>
              <th style={{ padding: 8, minWidth: 150 }}>Прогресс</th>
              <th style={{ padding: 8 }}>Действия</th>
            </tr>
          </thead>
          <tbody>
            {filteredOrders.map((o) => (
              <tr key={o.id} style={{ borderBottom: '1px solid #8A6636' }}>
                <td style={{ padding: 8 }}>#{o.id}</td>
                <td style={{ padding: 8 }}>{o.product}</td>
                <td style={{ padding: 8, textAlign: 'right' }}>{o.quantity}</td>
                <td style={{ padding: 8 }}>
                  <span style={{ color: getStatusColor(o.status), fontSize: 12, fontWeight: 600 }}>{o.status}</span>
                </td>
                <td style={{ padding: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={o.progress}
                      onChange={e => setProgress(o.id, parseInt(e.target.value))}
                      style={{ flex: 1, cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: 11, color: '#94A3B8', minWidth: 35 }}>{o.progress}%</span>
                  </div>
                </td>
                <td style={{ padding: 8 }}>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button onClick={() => updateOrderProgress(o.id, -10)} style={btnSmall}>−10</button>
                    <button onClick={() => updateOrderProgress(o.id, 10)} style={{...btnSmall, background: '#F7DE8A', color: '#0F172A', fontWeight: 700}}>+10</button>
                    <button onClick={() => deleteOrder(o.id)} style={deleteBtn}>🗑️</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        input[type="range"] {
          accent-color: #F7DE8A;
        }
      `}</style>
    </div>
  );
}

// Стили
const inputStyle = {
  padding: 10,
  background: '#0F172A',
  border: '1px solid #8A6636',
  borderRadius: 8,
  color: '#F8FAFC',
  fontSize: 13,
};

const selectStyle = {
  padding: 10,
  background: '#0F172A',
  border: '1px solid #8A6636',
  borderRadius: 8,
  color: '#F8FAFC',
  fontSize: 13,
  cursor: 'pointer',
};

const tableStyle = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: 12,
};

const btnPrimary = {
  padding: '8px 16px',
  background: '#F7DE8A',
  color: '#0F172A',
  border: 'none',
  borderRadius: 8,
  cursor: 'pointer',
  fontWeight: 700,
  fontSize: 13,
};

const btnSecondary = {
  padding: '8px 16px',
  background: '#8A6636',
  color: '#F8FAFC',
  border: 'none',
  borderRadius: 8,
  cursor: 'pointer',
  fontWeight: 600,
  fontSize: 13,
};

const btnDark = {
  padding: '8px 16px',
  background: '#334155',
  color: '#F8FAFC',
  border: 'none',
  borderRadius: 8,
  cursor: 'pointer',
  fontWeight: 600,
  fontSize: 13,
};

const btnSmall = {
  background: '#8A6636',
  color: '#fff',
  border: 'none',
  borderRadius: 4,
  cursor: 'pointer',
  fontSize: 12,
  padding: '4px 8px',
};

const deleteBtn = {
  background: 'none',
  border: 'none',
  color: '#EF4444',
  cursor: 'pointer',
  fontSize: 12,
};

const statCard = {
  background: '#1E293B',
  padding: 12,
  borderRadius: 8,
  border: '1px solid #8A6636',
  textAlign: 'center',
};

const statLabel = {
  fontSize: 10,
  color: '#94A3B8',
  marginBottom: 4,
};

const statValue = {
  fontSize: 18,
  fontWeight: 700,
};

const formCard = {
  marginBottom: 20,
  padding: 16,
  background: '#1E293B',
  borderRadius: 12,
  border: '1px solid #8A6636',
  animation: 'fadeIn 0.3s',
};

const formGrid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: 10,
};

export default Production;